/**
 * MCP Economy Tools — v5.8 "Live Economy" + v6.1 "Unified Budget"
 *
 * 7 tools:
 *   v5.8: check_agent_budget, get_usage_summary, get_creator_earnings
 *   v6.1: optimize_scene_budget, validate_marketplace_pricing, get_unified_budget_state
 *   WS-9: settle_creator_payout
 */

import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  AgentBudgetEnforcer,
  UsageMeter,
  CreatorRevenueAggregator,
  PaymentGateway,
  UnifiedBudgetOptimizer,
  DEFAULT_COST_FLOOR,
} from '@holoscript/framework/economy';
import type {
  BudgetState,
  AgentUsageSummary,
  CreatorEarnings,
  UsagePeriod,
  RevenuePeriod,
  UnifiedOptimizerConfig,
  TraitAllocation,
  UnifiedBudgetState,
  SettlementChain,
  SettlementMode,
  X402PaymentPayload,
  X402SettlementResult,
} from '@holoscript/framework/economy';
type ResourceUsageNode = any;

// =============================================================================
// SINGLETONS
// =============================================================================

let budgetEnforcer: AgentBudgetEnforcer | null = null;
let usageMeter: UsageMeter | null = null;
let revenueAggregator: CreatorRevenueAggregator | null = null;

function getBudgetEnforcer(): AgentBudgetEnforcer {
  if (!budgetEnforcer) budgetEnforcer = new AgentBudgetEnforcer();
  return budgetEnforcer;
}

function getUsageMeter(): UsageMeter {
  if (!usageMeter) usageMeter = new UsageMeter();
  return usageMeter;
}

function getRevenueAggregator(): CreatorRevenueAggregator {
  if (!revenueAggregator) revenueAggregator = new CreatorRevenueAggregator();
  return revenueAggregator;
}

export function resetEconomySingletons(): void {
  budgetEnforcer = null;
  usageMeter = null;
  revenueAggregator = null;
}

export interface ProtocolCreatorRevenueInput {
  creatorId: string;
  contentHash: string;
  grossAmount: number;
  collectorId: string;
  ledgerEntryId?: string;
}

export function recordProtocolCreatorRevenue({
  creatorId,
  contentHash,
  grossAmount,
  collectorId,
  ledgerEntryId,
}: ProtocolCreatorRevenueInput) {
  const aggregator = getRevenueAggregator();
  return aggregator.recordRevenue(
    creatorId,
    `protocol:${contentHash}`,
    grossAmount,
    collectorId,
    ledgerEntryId
  );
}

// =============================================================================
// TOOL DEFINITIONS
// =============================================================================

export const economyTools: Tool[] = [
  {
    name: 'check_agent_budget',
    description:
      'Check budget status for an agent. Returns spent, remaining, limit, enforcement mode, and circuit breaker state.',
    inputSchema: {
      type: 'object',
      properties: {
        agentId: { type: 'string', description: 'Agent ID to check budget for' },
        amount: {
          type: 'number',
          description: 'Optional: check if this spend amount would be authorized',
        },
      },
      required: ['agentId'],
    },
  },
  {
    name: 'get_usage_summary',
    description:
      'Get usage summary for an agent or globally. Shows per-tool cost breakdown, free-tier status, and top tools.',
    inputSchema: {
      type: 'object',
      properties: {
        agentId: { type: 'string', description: 'Agent ID (omit for global summary)' },
        period: {
          type: 'string',
          enum: ['hourly', 'daily', 'monthly'],
          description: 'Aggregation period (default: monthly)',
        },
        topToolsLimit: {
          type: 'number',
          description: 'Number of top tools to return (default: 10)',
        },
      },
    },
  },
  {
    name: 'get_creator_earnings',
    description:
      'Get earnings for a plugin creator. Shows revenue by plugin, platform fees, payout eligibility, and payout history.',
    inputSchema: {
      type: 'object',
      properties: {
        creatorId: { type: 'string', description: 'Creator ID to get earnings for' },
        period: {
          type: 'string',
          enum: ['daily', 'weekly', 'monthly', 'all-time'],
          description: 'Aggregation period (default: monthly)',
        },
      },
      required: ['creatorId'],
    },
  },
  {
    name: 'settle_creator_payout',
    description:
      'Settle an unpaid creator earning through the x402 micro-payment path, record the payout, and return a receipt. Requires an X-PAYMENT header or decoded x402 payment payload; on-chain settlement is disabled by default so one-cent test payouts stay in the micro-ledger.',
    inputSchema: {
      type: 'object',
      properties: {
        creatorId: { type: 'string', description: 'Creator ID with unpaid earnings' },
        recipientWallet: {
          type: 'string',
          description: 'Seat wallet receiving the payout; must match the x402 authorization recipient',
        },
        amountBaseUnits: {
          type: 'number',
          description: 'USDC base units to pay out (10000 = $0.01). Defaults to unpaid net earnings.',
        },
        period: {
          type: 'string',
          enum: ['daily', 'weekly', 'monthly', 'all-time'],
          description: 'Earnings period to settle (default: all-time)',
        },
        chain: {
          type: 'string',
          enum: ['base', 'base-sepolia', 'solana', 'solana-devnet'],
          description: 'x402 settlement chain (default: base-sepolia)',
        },
        xPaymentHeader: {
          type: 'string',
          description: 'Base64-encoded X-PAYMENT header. Alternative to paymentPayload.',
        },
        paymentPayload: {
          type: 'object',
          description: 'Decoded x402 payment payload. Alternative to xPaymentHeader.',
        },
        resource: {
          type: 'string',
          description: 'Receipt resource label (default: creator-payout:<creatorId>)',
        },
        allowOnChain: {
          type: 'boolean',
          description: 'Opt in to on-chain settlement for amounts at/above the micro threshold.',
        },
      },
      required: ['creatorId', 'recipientWallet'],
    },
  },

  // ── Unified Budget Tools (v6.1) ──

  {
    name: 'optimize_scene_budget',
    description:
      "Run equimarginal allocation on a scene's traits against a platform budget. Returns which traits to include, LOD levels, and what to shed — sorted by value/cost ratio so the most efficient traits survive.",
    inputSchema: {
      type: 'object',
      properties: {
        platform: {
          type: 'string',
          enum: ['quest3', 'desktop-vr', 'webgpu', 'mobile-ar'],
          description: 'Target platform for resource limits',
        },
        nodes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'Object name (e.g., "Player", "Tree")' },
              traits: {
                type: 'array',
                items: { type: 'string' },
                description: 'Trait names (e.g., ["@mesh", "@particle"])',
              },
              count: { type: 'number', description: 'Instance count (default: 1)' },
              calls: {
                type: 'array',
                items: { type: 'string' },
                description: 'Built-in function calls (e.g., ["spawn", "playSound"])',
              },
            },
            required: ['name', 'traits'],
          },
          description: 'Scene objects with their traits and instance counts',
        },
        maxLOD: { type: 'number', description: 'Maximum LOD level to consider (default: 4)' },
        economicBudget: {
          type: 'number',
          description: 'Economic budget in USDC base units (optional)',
        },
        economicSpent: {
          type: 'number',
          description: 'Economic spend so far in USDC base units (optional)',
        },
      },
      required: ['platform', 'nodes'],
    },
  },
  {
    name: 'validate_marketplace_pricing',
    description:
      'Validate that a marketplace trait listing price meets the resource cost floor. Prevents economic denial-of-rendering attacks where a cheap trait consumes massive GPU resources.',
    inputSchema: {
      type: 'object',
      properties: {
        traitName: { type: 'string', description: 'Trait name to validate (e.g., "@gaussian")' },
        listPrice: {
          type: 'number',
          description: 'Proposed listing price in USDC base units (6 decimals, so 1000000 = $1.00)',
        },
        instanceCount: { type: 'number', description: 'Expected instance count (default: 1)' },
        platform: {
          type: 'string',
          enum: ['quest3', 'desktop-vr', 'webgpu', 'mobile-ar'],
          description: 'Target platform (default: quest3)',
        },
      },
      required: ['traitName', 'listPrice'],
    },
  },
  {
    name: 'get_unified_budget_state',
    description:
      'Get a unified view of budget pressure across economy + rendering for an agent. Returns economic pressure, resource pressure per category, suggested LOD, and shed candidates. NOTE: this is per-agent economic + render-resource (triangle/draw-call) budget — it does NOT report live fleet/GPU rental spend; for that use the vast spend ledger (scripts/reconcile-vast-ledger.mjs).',
    inputSchema: {
      type: 'object',
      properties: {
        agentId: { type: 'string', description: 'Agent identifier' },
        platform: {
          type: 'string',
          enum: ['quest3', 'desktop-vr', 'webgpu', 'mobile-ar'],
          description: 'Target platform (default: quest3)',
        },
        nodes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'Object name' },
              traits: { type: 'array', items: { type: 'string' }, description: 'Trait names' },
              count: { type: 'number', description: 'Instance count (default: 1)' },
              calls: {
                type: 'array',
                items: { type: 'string' },
                description: 'Built-in function calls',
              },
            },
            required: ['name', 'traits'],
          },
          description: 'Scene objects with their traits',
        },
        economicBudget: { type: 'number', description: 'Economic budget in USDC base units' },
        economicSpent: { type: 'number', description: 'Economic spend so far in USDC base units' },
      },
      required: ['agentId'],
    },
  },
];

// =============================================================================
// HANDLER
// =============================================================================

export async function handleEconomyTool(
  name: string,
  args: Record<string, unknown>
): Promise<unknown> {
  switch (name) {
    case 'check_agent_budget':
      return handleCheckAgentBudget(args);
    case 'get_usage_summary':
      return handleGetUsageSummary(args);
    case 'get_creator_earnings':
      return handleGetCreatorEarnings(args);
    case 'settle_creator_payout':
      return handleSettleCreatorPayout(args);
    case 'optimize_scene_budget':
      return handleOptimizeSceneBudget(args);
    case 'validate_marketplace_pricing':
      return handleValidateMarketplacePricing(args);
    case 'get_unified_budget_state':
      return handleGetUnifiedBudgetState(args);
    default:
      throw new Error(`Unknown economy tool: ${name}`);
  }
}

// =============================================================================
// IMPLEMENTATIONS
// =============================================================================

function handleCheckAgentBudget(args: Record<string, unknown>): {
  agentId: string;
  state: BudgetState | null;
  authorization?: {
    authorized: boolean;
    reason?: string;
    warning?: boolean;
    warningMessage?: string;
  };
} {
  const agentId = args.agentId as string;
  const enforcer = getBudgetEnforcer();

  const state = enforcer.getState(agentId) ?? null;

  // If amount is provided, check authorization
  if (args.amount !== undefined) {
    const amount = args.amount as number;
    const auth = enforcer.authorize(agentId, amount);
    return {
      agentId,
      state: auth.state,
      authorization: {
        authorized: auth.authorized,
        reason: auth.reason,
        warning: auth.warning,
        warningMessage: auth.warningMessage,
      },
    };
  }

  return { agentId, state };
}

function handleGetUsageSummary(args: Record<string, unknown>): {
  agentId?: string;
  period: string;
  summary: {
    totalCalls: number;
    totalCost: number;
    freeTierCalls: number;
    paidCalls: number;
    paidCost: number;
  };
  topTools?: Array<{ toolId: string; calls: number; cost: number }>;
  freeTierRemaining?: number;
} {
  const meter = getUsageMeter();
  const period = (args.period as UsagePeriod) ?? 'monthly';
  const topToolsLimit = (args.topToolsLimit as number) ?? 10;
  const agentId = args.agentId as string | undefined;

  if (agentId) {
    const usage: AgentUsageSummary = meter.getAgentUsage(agentId, period);
    return {
      agentId,
      period,
      summary: {
        totalCalls: usage.total.totalCalls,
        totalCost: usage.total.totalCost,
        freeTierCalls: usage.total.freeTierCalls,
        paidCalls: usage.total.paidCalls,
        paidCost: usage.total.paidCost,
      },
      freeTierRemaining: usage.freeTierRemaining,
    };
  }

  // Global summary
  const global = meter.getGlobalUsage(period);
  const topTools = meter.getTopTools(period, topToolsLimit);

  return {
    period,
    summary: {
      totalCalls: global.totalCalls,
      totalCost: global.totalCost,
      freeTierCalls: global.freeTierCalls,
      paidCalls: global.paidCalls,
      paidCost: global.paidCost,
    },
    topTools,
  };
}

function handleGetCreatorEarnings(args: Record<string, unknown>): {
  creatorId: string;
  period: string;
  earnings: {
    totalGross: number;
    totalFees: number;
    totalNet: number;
    eventCount: number;
    byPlugin: Array<{
      pluginId: string;
      grossAmount: number;
      netAmount: number;
      eventCount: number;
      uniquePayers: number;
    }>;
  };
  payouts: Array<{
    id: string;
    amount: number;
    method: string;
    status: string;
    paidAt: string;
    transactionHash?: string;
  }>;
  platformFeeRate: number;
} {
  const aggregator = getRevenueAggregator();
  const creatorId = args.creatorId as string;
  const period = (args.period as RevenuePeriod) ?? 'monthly';

  const earnings: CreatorEarnings = aggregator.getCreatorEarnings(creatorId, period);
  const payouts = aggregator.getCreatorPayouts(creatorId, period);

  const byPlugin = [...earnings.byPlugin.entries()].map(([pluginId, rev]) => ({
    pluginId,
    grossAmount: rev.grossAmount,
    netAmount: rev.netAmount,
    eventCount: rev.eventCount,
    uniquePayers: rev.uniquePayers.size,
  }));

  return {
    creatorId,
    period,
    earnings: {
      totalGross: earnings.totalGross,
      totalFees: earnings.totalFees,
      totalNet: earnings.totalNet,
      eventCount: earnings.eventCount,
      byPlugin,
    },
    payouts: payouts.map((p: any) => ({
      id: p.id,
      amount: p.amount,
      method: p.method,
      status: p.status,
      paidAt: p.paidAt,
      transactionHash: p.transactionHash,
    })),
    platformFeeRate: aggregator.getPlatformFeeRate(),
  };
}

type EconomyError = { status: 'error'; error: string; message: string };

type CreatorPayoutSettlementReceipt = {
  schema: 'holoscript.creator-payout.receipt.v1';
  tool: 'settle_creator_payout';
  creatorId: string;
  period: RevenuePeriod;
  amountBaseUnits: number;
  amountUSDC: number;
  recipientWallet: string;
  resource: string;
  unpaidBefore: number;
  earningsBefore: {
    totalGross: number;
    totalFees: number;
    totalNet: number;
    eventCount: number;
  };
  settlement: Pick<
    X402SettlementResult,
    'success' | 'transaction' | 'network' | 'payer' | 'errorReason' | 'mode' | 'settledAt'
  >;
  payoutRecord: {
    id: string;
    amount: number;
    method: string;
    status: string;
    paidAt: string;
    transactionHash?: string;
  };
  x402: {
    chain: SettlementChain;
    settlementMode: SettlementMode;
    microPaymentThresholdBaseUnits: number;
    signatureVerification: string;
  };
};

function stringParam(
  value: unknown,
  fieldName: string
): string | EconomyError {
  if (typeof value !== 'string' || value.trim() === '') {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: `${fieldName} must be a non-empty string`,
    };
  }
  return value;
}

function parseRevenuePeriod(value: unknown, fallback: RevenuePeriod): RevenuePeriod | EconomyError {
  if (value === undefined || value === null) return fallback;
  if (value === 'daily' || value === 'weekly' || value === 'monthly' || value === 'all-time') {
    return value;
  }
  return {
    status: 'error',
    error: 'INVALID_PARAMS',
    message: 'period must be one of daily, weekly, monthly, all-time',
  };
}

function parseSettlementChain(value: unknown, fallback: SettlementChain): SettlementChain | EconomyError {
  if (value === undefined || value === null) return fallback;
  if (value === 'base' || value === 'base-sepolia' || value === 'solana' || value === 'solana-devnet') {
    return value;
  }
  return {
    status: 'error',
    error: 'INVALID_PARAMS',
    message: 'chain must be one of base, base-sepolia, solana, solana-devnet',
  };
}

function parseAmountBaseUnits(value: unknown, fallback: number): number | EconomyError {
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: 'amountBaseUnits must be a positive safe integer',
    };
  }
  return value;
}

function completedPayoutTotal(
  payouts: ReturnType<CreatorRevenueAggregator['getCreatorPayouts']>
): number {
  return payouts.reduce((sum, payout) => sum + (payout.status === 'completed' ? payout.amount : 0), 0);
}

async function handleSettleCreatorPayout(
  args: Record<string, unknown>
): Promise<
  | {
      status: 'success';
      creatorId: string;
      receipt: CreatorPayoutSettlementReceipt;
    }
  | {
      status: 'settlement_failed';
      creatorId: string;
      settlement: X402SettlementResult;
      message: string;
    }
  | EconomyError
> {
  const creatorId = stringParam(args.creatorId, 'creatorId');
  if (typeof creatorId !== 'string') return creatorId;

  const recipientWallet = stringParam(args.recipientWallet, 'recipientWallet');
  if (typeof recipientWallet !== 'string') return recipientWallet;

  const period = parseRevenuePeriod(args.period, 'all-time');
  if (typeof period !== 'string') return period;

  const chain = parseSettlementChain(args.chain, 'base-sepolia');
  if (typeof chain !== 'string') return chain;

  const aggregator = getRevenueAggregator();
  const earnings = aggregator.getCreatorEarnings(creatorId, period);
  const priorPayouts = aggregator.getCreatorPayouts(creatorId, period);
  const unpaidBefore = earnings.totalNet - completedPayoutTotal(priorPayouts);
  const amountBaseUnits = parseAmountBaseUnits(args.amountBaseUnits, unpaidBefore);
  if (typeof amountBaseUnits !== 'number') return amountBaseUnits;

  if (amountBaseUnits > unpaidBefore) {
    return {
      status: 'error',
      error: 'INSUFFICIENT_EARNINGS',
      message: `creator ${creatorId} has ${unpaidBefore} unpaid base units; requested ${amountBaseUnits}`,
    };
  }

  const microPaymentThresholdBaseUnits = 100_000;
  if (args.allowOnChain !== true && amountBaseUnits >= microPaymentThresholdBaseUnits) {
    return {
      status: 'error',
      error: 'ON_CHAIN_DISABLED',
      message:
        'settle_creator_payout defaults to x402 micro-ledger settlement only; pass allowOnChain=true to use an external facilitator for larger amounts',
    };
  }

  const payment =
    typeof args.xPaymentHeader === 'string'
      ? args.xPaymentHeader
      : typeof args.paymentPayload === 'object' && args.paymentPayload !== null
        ? (args.paymentPayload as X402PaymentPayload)
        : null;

  if (!payment) {
    return {
      status: 'error',
      error: 'MISSING_X402_PAYMENT',
      message: 'xPaymentHeader or paymentPayload is required to settle a creator payout',
    };
  }

  const resource =
    typeof args.resource === 'string' && args.resource.trim() !== ''
      ? args.resource
      : `creator-payout:${creatorId}`;

  const gateway = new PaymentGateway({
    recipientAddress: recipientWallet,
    chain,
    microPaymentThreshold: microPaymentThresholdBaseUnits,
    optimisticExecution: false,
  });

  const settlement = await gateway.settlePayment(payment, resource, String(amountBaseUnits));
  gateway.dispose();

  if (!settlement.success) {
    return {
      status: 'settlement_failed',
      creatorId,
      settlement,
      message: settlement.errorReason ?? 'x402 settlement failed',
    };
  }

  const payoutRecord = aggregator.recordPayout(
    creatorId,
    amountBaseUnits,
    settlement.mode === 'in_memory' ? 'batch_settlement' : 'usdc_transfer',
    settlement.transaction ?? undefined
  );

  return {
    status: 'success',
    creatorId,
    receipt: {
      schema: 'holoscript.creator-payout.receipt.v1',
      tool: 'settle_creator_payout',
      creatorId,
      period,
      amountBaseUnits,
      amountUSDC: amountBaseUnits / 1_000_000,
      recipientWallet,
      resource,
      unpaidBefore,
      earningsBefore: {
        totalGross: earnings.totalGross,
        totalFees: earnings.totalFees,
        totalNet: earnings.totalNet,
        eventCount: earnings.eventCount,
      },
      settlement: {
        success: settlement.success,
        transaction: settlement.transaction,
        network: settlement.network,
        payer: settlement.payer,
        errorReason: settlement.errorReason,
        mode: settlement.mode,
        settledAt: settlement.settledAt,
      },
      payoutRecord: {
        id: payoutRecord.id,
        amount: payoutRecord.amount,
        method: payoutRecord.method,
        status: payoutRecord.status,
        paidAt: payoutRecord.paidAt,
        transactionHash: payoutRecord.transactionHash,
      },
      x402: {
        chain,
        settlementMode: settlement.mode,
        microPaymentThresholdBaseUnits,
        signatureVerification:
          settlement.mode === 'in_memory'
            ? 'structural x402 payload verified by X402Facilitator; no external facilitator call for micro-ledger settlement'
            : 'external facilitator settlement',
      },
    },
  };
}

// =============================================================================
// UNIFIED BUDGET TOOLS (v6.1)
// =============================================================================

function parseNodes(raw: unknown): ResourceUsageNode[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((n: Record<string, unknown>) => ({
    name: (n.name as string) || 'unnamed',
    traits: Array.isArray(n.traits) ? (n.traits as string[]) : [],
    count: typeof n.count === 'number' ? n.count : 1,
    calls: Array.isArray(n.calls) ? (n.calls as string[]) : [],
  }));
}

function handleOptimizeSceneBudget(args: Record<string, unknown>): {
  platform: string;
  allocations: TraitAllocation[];
  summary: {
    totalTraits: number;
    includedTraits: number;
    excludedTraits: number;
    degradedTraits: number;
  };
} {
  const platform = (args.platform as string) || 'quest3';
  const nodes = parseNodes(args.nodes);
  const maxLOD = typeof args.maxLOD === 'number' ? args.maxLOD : 4;

  const config: UnifiedOptimizerConfig = {
    platform,
    costFloor: DEFAULT_COST_FLOOR,
    economicBudget: typeof args.economicBudget === 'number' ? args.economicBudget : undefined,
    economicSpent: typeof args.economicSpent === 'number' ? args.economicSpent : undefined,
  };

  const optimizer = new UnifiedBudgetOptimizer(config);
  const allocations = optimizer.allocate(nodes, maxLOD);

  const included = allocations.filter((a: any) => a.included);
  const excluded = allocations.filter((a: any) => !a.included);
  const degraded = included.filter((a: any) => a.lodLevel > 0);

  return {
    platform,
    allocations,
    summary: {
      totalTraits: allocations.length,
      includedTraits: included.length,
      excludedTraits: excluded.length,
      degradedTraits: degraded.length,
    },
  };
}

function handleValidateMarketplacePricing(args: Record<string, unknown>): {
  traitName: string;
  listPrice: number;
  instanceCount: number;
  valid: boolean;
  floor: number;
  deficit: number;
  message: string;
  costBreakdown: {
    baseFee: number;
    resourceCost: number;
  };
} {
  const traitName = args.traitName as string;
  const listPrice = args.listPrice as number;
  const instanceCount = typeof args.instanceCount === 'number' ? args.instanceCount : 1;
  const platform = (args.platform as string) || 'quest3';

  const optimizer = new UnifiedBudgetOptimizer({
    platform,
    costFloor: DEFAULT_COST_FLOOR,
  });

  const result = optimizer.validateMarketplacePrice(traitName, listPrice, instanceCount);
  const baseFee = DEFAULT_COST_FLOOR.baseFee;

  return {
    traitName,
    listPrice,
    instanceCount,
    valid: result.valid,
    floor: result.floor,
    deficit: result.deficit,
    message: result.message,
    costBreakdown: {
      baseFee,
      resourceCost: result.floor - baseFee,
    },
  };
}

function handleGetUnifiedBudgetState(args: Record<string, unknown>): UnifiedBudgetState {
  const agentId = args.agentId as string;
  const platform = (args.platform as string) || 'quest3';
  const nodes = parseNodes(args.nodes);
  const economicBudget = typeof args.economicBudget === 'number' ? args.economicBudget : undefined;
  const economicSpent = typeof args.economicSpent === 'number' ? args.economicSpent : undefined;

  const optimizer = new UnifiedBudgetOptimizer({
    platform,
    costFloor: DEFAULT_COST_FLOOR,
    economicBudget,
    economicSpent,
  });

  return optimizer.getUnifiedState(agentId, nodes, economicSpent, economicBudget);
}
